import { useCallback, useState } from "react";

/**
 * Custom hook for managing alert state and notifications in React components.
 * Provides functionality to show and hide alerts with customizable messages and types.
 *
 * @param {Object} initialState - Initial configuration for the alert state
 * @param {boolean} initialState.alertOpen - Initial visibility state of the alert
 * @param {string} initialState.alertType - Initial alert type (e.g., 'success', 'error')
 * @param {string} initialState.alertMsg - Initial alert message
 *
 * @returns {Object} Alert state and control methods
 * @returns {Object} .alertState - Current alert state object
 * @returns {Function} .showAlert - Method to display an alert with type and message
 * @returns {Function} .closeAlert - Method to close the current alert
 *
 * @example
 * const { alertState, showAlert, closeAlert } = useAlert();
 * showAlert('success', 'Operation completed');
 */
const useAlert = (initialState = { alertOpen: false, alertType: "", alertMsg: "" }) => {
  const [alertState, setAlertState] = useState(initialState);

  // Stable identities — callers put these in useCallback/useEffect deps, so a new
  // function each render would re-run their effects on every alert.
  const showAlert = useCallback((type, message) => {
    setAlertState({
      alertOpen: true,
      alertType: type,
      alertMsg: message,
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState((prevState) => ({ ...prevState, alertOpen: false }));
  }, []);

  return {
    alertState,
    showAlert,
    closeAlert,
  };
};

export default useAlert;
